/**
 * Layer visibility for the read-only composite plan (patterns §44).
 *
 * The composite SVG is layer-major (internal/svgplan): one
 * `<g class="underlays|spaces|walls|openings|runs|equipment|labels">` per layer,
 * each holding one `<g class="representation">` per contributing source. The
 * viewer never re-fetches to hide a layer: it reads that structure once and
 * writes a scoped stylesheet next to the inlined document.
 *
 * The choices are a per-viewer convenience, kept per asset in localStorage.
 */

// `runs` are the network runs (ducts, pipes, cable trays), the "Networks" layer.
export const VECTOR_LAYERS = ['spaces', 'walls', 'openings', 'runs', 'equipment'] as const
export type VectorLayer = typeof VECTOR_LAYERS[number]

/** One raster source of the frame, as the SVG lists it. */
export interface PlanUnderlay {
    /** Stable key: the registration when the source is registered, else the representation. */
    key: string
    repUid: string
    registrationUid: string | null
    /** The SVG `<title>`: the owning asset's name. The caller may have a better label. */
    title: string
}

export interface UnderlayState {
    visible: boolean
    opacity: number
}

export interface CompositePlanLayers {
    underlaysOn: boolean
    /** Missing key = visible at full opacity, so a newly registered sheet shows up. */
    underlays: Record<string, UnderlayState>
    vector: Record<VectorLayer, boolean>
    /** Network codes (`network_kind`) whose runs are hidden: one network, or a whole family at once. */
    hiddenNetworks: string[]
}

export const DEFAULT_LAYERS: CompositePlanLayers = {
    underlaysOn: true,
    underlays: {},
    vector: { spaces: true, walls: true, openings: true, runs: true, equipment: true },
    hiddenNetworks: [],
}

/** All underlays off, every vector layer on: the redrawn plan alone. */
export function redrawnOnly(current: CompositePlanLayers): CompositePlanLayers {
    return { ...current, underlaysOn: false, vector: { ...DEFAULT_LAYERS.vector }, hiddenNetworks: [] }
}

export function underlayState(layers: CompositePlanLayers, key: string): UnderlayState {
    return layers.underlays[key] ?? { visible: true, opacity: 1 }
}

export interface PlanStructure {
    underlays: PlanUnderlay[]
    /** The vector layers the document actually carries. */
    vectorLayers: VectorLayer[]
}

/** Reads the layer structure out of the composite SVG text. */
export function parsePlanStructure(svg: string): PlanStructure {
    if (!svg || typeof DOMParser === 'undefined') return { underlays: [], vectorLayers: [] }
    const doc = new DOMParser().parseFromString(svg, 'image/svg+xml')
    const root = doc.documentElement
    if (!root || root.nodeName !== 'svg') return { underlays: [], vectorLayers: [] }

    const underlays: PlanUnderlay[] = []
    const seen = new Set<string>()
    for (const layer of Array.from(root.children)) {
        if (layer.getAttribute('class') !== 'underlays') continue
        for (const group of Array.from(layer.children)) {
            const repUid = group.getAttribute('data-representation-uid') ?? ''
            const registrationUid = group.getAttribute('data-registration-uid') || null
            const key = registrationUid ?? repUid
            if (!key || seen.has(key)) continue
            seen.add(key)
            const title = Array.from(group.children).find((c) => c.nodeName === 'title')?.textContent ?? ''
            underlays.push({ key, repUid, registrationUid, title })
        }
    }

    const present = new Set(Array.from(root.children).map((c) => c.getAttribute('class') ?? ''))
    const vectorLayers = VECTOR_LAYERS.filter((layer) => present.has(layer))
    return { underlays, vectorLayers }
}

const UID = /^[0-9a-fA-F-]+$/
const NETWORK_CODE = /^[a-z][a-z0-9_]*$/

function underlaySelector(scope: string, u: PlanUnderlay): string | null {
    if (u.registrationUid) {
        return UID.test(u.registrationUid)
            ? `${scope} g.underlays > g.representation[data-registration-uid="${u.registrationUid}"]`
            : null
    }
    // An implicit source (the frame's own plan) carries no registration.
    return UID.test(u.repUid)
        ? `${scope} g.underlays > g.representation[data-representation-uid="${u.repUid}"]:not([data-registration-uid])`
        : null
}

/**
 * The stylesheet that applies `layers` to the inlined document under `scope`
 * (a selector naming the wrapper). Empty when everything is at its default.
 */
export function layersStyleSheet(scope: string, layers: CompositePlanLayers, underlays: PlanUnderlay[]): string {
    const rules: string[] = []
    if (!layers.underlaysOn) {
        rules.push(`${scope} g.underlays { display: none; }`)
    } else {
        for (const u of underlays) {
            const state = underlayState(layers, u.key)
            const selector = underlaySelector(scope, u)
            if (!selector) continue
            if (!state.visible) rules.push(`${selector} { display: none; }`)
            else if (state.opacity < 1) rules.push(`${selector} { opacity: ${Math.max(0, state.opacity)}; }`)
        }
    }
    for (const layer of VECTOR_LAYERS) {
        if (layers.vector[layer]) continue
        // The networks' legend is a sibling group: it goes with its runs.
        rules.push(layer === 'runs'
            ? `${scope} g.runs, ${scope} g.runs-legend { display: none; }`
            : `${scope} g.${layer} { display: none; }`)
    }
    for (const code of layers.hiddenNetworks) {
        // Codes are lower snake case (the API refuses anything else): a stored
        // value that is not one never reaches the stylesheet.
        if (NETWORK_CODE.test(code)) rules.push(`${scope} g.runs g[data-network="${code}"] { display: none; }`)
    }
    return rules.join('\n')
}

export function storageKey(org: string, assetUid: string): string {
    return `composite-plan-layers:${org}:${assetUid}`
}

export function loadLayers(org: string, assetUid: string): CompositePlanLayers {
    try {
        const raw = localStorage.getItem(storageKey(org, assetUid))
        if (!raw) return DEFAULT_LAYERS
        const parsed = JSON.parse(raw) as Partial<CompositePlanLayers>
        const underlays: Record<string, UnderlayState> = {}
        for (const [key, value] of Object.entries(parsed.underlays ?? {})) {
            if (!value || typeof value !== 'object') continue
            underlays[key] = {
                visible: value.visible !== false,
                opacity: typeof value.opacity === 'number' ? Math.min(1, Math.max(0, value.opacity)) : 1,
            }
        }
        const vector = { ...DEFAULT_LAYERS.vector }
        for (const layer of VECTOR_LAYERS) {
            if (parsed.vector?.[layer] === false) vector[layer] = false
        }
        const hiddenNetworks = Array.isArray(parsed.hiddenNetworks)
            ? parsed.hiddenNetworks.filter((c): c is string => typeof c === 'string' && NETWORK_CODE.test(c))
            : []
        return { underlaysOn: parsed.underlaysOn !== false, underlays, vector, hiddenNetworks }
    } catch {
        return DEFAULT_LAYERS
    }
}

export function saveLayers(org: string, assetUid: string, layers: CompositePlanLayers): void {
    try {
        localStorage.setItem(storageKey(org, assetUid), JSON.stringify(layers))
    } catch {
        // Private mode or a full quota: the choice simply does not survive a reload.
    }
}

/** Whether a layer panel is open: one per-viewer choice for every plan view. Closed by default. */
const PANEL_OPEN_KEY = 'plan-layers-open'

export function loadPanelOpen(): boolean {
    try {
        return localStorage.getItem(PANEL_OPEN_KEY) === 'true'
    } catch {
        return false
    }
}

export function savePanelOpen(open: boolean): void {
    try {
        localStorage.setItem(PANEL_OPEN_KEY, String(open))
    } catch {
        // A per-viewer convenience: losing it costs nothing.
    }
}
